import { AfterViewInit, Component, ElementRef, HostListener, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthApiService } from '../../../services/auth.api.service';
import { UserState } from '../../../states/user-state';
import { BrowserStorageService } from '../../../services/browser-storage.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthenticationManagerOperation } from '../../../../shared/operations/auth/authentication-manager.operation';
import { catchError, of, tap } from 'rxjs';
import { MessageResponse } from '../../../services/auth.api.service';
import { LoanApplicationService } from '../../../services/loan/loan-application.api.service';
import CBValidators from '../../../../shared/validators';
import { MatDialog } from '@angular/material/dialog';
import { DialogInfoComponent } from '../../dialog/dialog-info/dialog-info.component';
import { environment } from '../../../../../environments/environment';
import { scrollToFirstInvalidFormcontrol } from '../../../../shared/form-groups';
import { userFacingErrorMessage } from '../../../utils/user-facing-error';

@Component({
    selector: 'app-sign-up',
    templateUrl: './sign-up.component.html',
    styleUrl: './sign-up.component.scss',
    standalone: false
})
export class SignUpComponent implements AfterViewInit {
    private loanId: string | undefined = undefined;
    private inviteToken: string | undefined = undefined;
    private prefillEmail: string | undefined = undefined;

    email: string = '';
    password: string = '';
    hide = true;
    keepLoggedIn = false;
    authenticationManager: AuthenticationManagerOperation;
    @ViewChild('emailInput') emailInputElement!: ElementRef;
    fg: FormGroup;
    err: string = "";
    msg: string = "";

    readonly onlineBankingUrl = environment.onlineBankingCommercialLoanUrl;
    readonly contactPhone = '(801) 375-1000';
    readonly contactUrl = 'https://cbutah.com/central-bank/contact-us/';

    constructor(
        private route: ActivatedRoute,
        private authService: AuthApiService,
        private userState: UserState,
        private router: Router,
        private browserStorageService: BrowserStorageService, // Inject LocalStorageService
        private fb: FormBuilder,
        private loanService: LoanApplicationService,
        private dialog: MatDialog,
    ) {
        route.queryParams.subscribe(p => {
            this.loanId = p['loanId'];
            this.inviteToken = p['inviteToken'];
            this.prefillEmail = p['email'];
            // if (!this.loanId) router.navigate(['/login']);
        });

        this.authenticationManager = new AuthenticationManagerOperation(authService, userState, router, browserStorageService);
        this.fg = this.fb.group({
            username: this.fb.nonNullable.control('', [Validators.required, CBValidators.email]),
            password: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(8)]),
            repeatPassword: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(8)]),
            fullName: this.fb.nonNullable.control('', [Validators.required, Validators.minLength(1), Validators.maxLength(255)]),
            esignConsent: this.fb.nonNullable.control(false, [Validators.requiredTrue]),
            keep: this.fb.nonNullable.control(''),
        });

        if (this.prefillEmail) {
            this.usernameControl.setValue(this.prefillEmail);
        }
    }

    ngAfterViewInit() {
        if (this.emailInputElement) {
            this.emailInputElement.nativeElement.focus();
        }
    }

    signup() {
        this.err = '';
        this.msg = '';
        const email = this.usernameControl.value;
        const password = this.passwordControl.value;
        const repeatPassword = this.repeatPasswordControl.value;
        const fullName = this.fullNameControl.value?.trim() ?? '';

        if (password !== repeatPassword) {
            const err = { passwordMatch: 'Passwords do not match' };
            this.passwordControl.setErrors(err);
            this.repeatPasswordControl.setErrors(err);
            return;
        }

        if (!this.fg.valid) {
            this.fg.markAllAsTouched();
            scrollToFirstInvalidFormcontrol();
            return;
        }

        if (this.loanId) {
            this.authenticationManager.nonjointSignup(email, password, fullName, this.loanId, this.esignConsentControl.value)
                .pipe(
                    tap((res: MessageResponse) => {
                        const fallback =
                            'If you are setting up a new account, check your email for next steps when applicable. Then click Continue to sign in.';
                        const bodyText = res?.message?.trim() ? res.message : fallback;
                        this.msg = bodyText;
                        const ref = this.dialog.open(DialogInfoComponent, {
                            disableClose: true,
                            data: {
                                header: 'Next steps',
                                body: bodyText,
                                buttonText: 'Continue',
                            },
                        });
                        ref.afterClosed().subscribe(() => {
                            const qp = this.inviteToken ? { inviteToken: this.inviteToken, email } : undefined;
                            this.router.navigate(['/login'], { queryParams: qp as any });
                        });
                    }),
                    catchError((error: any) => {
                        console.error('Signup failed', error);
                        this.err = userFacingErrorMessage(error, 'signup');
                        return of(null); // Return an observable to complete the stream
                    })
                )
                .subscribe();
        }
        // else normal signup
        else {
            this.authenticationManager.signup(email, password, fullName, this.esignConsentControl.value)
                .pipe(
                    tap((res: MessageResponse) => {
                        const fallback =
                            'If you are setting up a new account, check your email for next steps when applicable. Then click Continue to sign in.';
                        const bodyText = res?.message?.trim() ? res.message : fallback;
                        this.msg = bodyText;
                        const ref = this.dialog.open(DialogInfoComponent, {
                            disableClose: true,
                            data: {
                                header: 'Next steps',
                                body: bodyText,
                                buttonText: 'Continue',
                            },
                        });
                        ref.afterClosed().subscribe(() => {
                            const qp = this.inviteToken ? { inviteToken: this.inviteToken, email } : undefined;
                            this.router.navigate(['/login'], { queryParams: qp as any });
                        });
                    }),
                    catchError((error: any) => {
                        console.error('Signup failed', error);
                        this.err = userFacingErrorMessage(error, 'signup');
                        return of(null); // Return an observable to complete the stream
                    })
                )
                .subscribe();
        }
    }

    // login(): void {
    //   this.authenticationManager
    //     .login(this.usernameControl.value, this.passwordControl.value, this.keepLoggedIn)
    //     .pipe(
    //       tap((user: UserModel) => {
    //         this.router.navigate(['/loan/application-list']);
    //       }),
    //       catchError((error: any) => {
    //         console.error('Login failed', error);
    //         return of(null); // Return an observable to complete the stream
    //       })
    //     )
    //     .subscribe();
    // }

    clickEvent(event: MouseEvent): void {
        this.hide = !this.hide;
        event.stopPropagation();
    }

    get usernameControl() {
        return this.fg.get('username') as FormControl;
    }
    get passwordControl() {
        return this.fg.get('password') as FormControl;
    }
    get repeatPasswordControl() {
        return this.fg.get('repeatPassword') as FormControl;
    }
    get fullNameControl() {
        return this.fg.get('fullName') as FormControl;
    }
    get keepControl() {
        return this.fg.get('keep') as FormControl;
    }
    get esignConsentControl() {
        return this.fg.get('esignConsent') as FormControl;
    }

    @HostListener('document:keydown.enter', ['$event'])
    handleEnterKey(event: KeyboardEvent): void {
        this.signup();
    }
}
